import React from 'react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { AddSuccessAlert, AddErrorAlert } from '@SonarCX/slices/alert'
import { selectAllTasksTable } from '@SonarCX/slices/allTasksTable'
import { useTaskAPI } from '@SonarCX/providers/TaskAPIProvider'

const useTaskMultiEdit = () => {
  const dispatch = useAppDispatch()

  const { selected } = useAppSelector(selectAllTasksTable)

  const { patchTasks } = useTaskAPI()

  const { numTasks, suffix, plural } = React.useMemo(() => {
    const numTasks = selected.length
    const suffix = numTasks === 1 ? '' : 's'
    const plural = numTasks === 1 ? 'this' : 'these'
    return { numTasks, suffix, plural }
  }, [selected.length])

  const showMessage = React.useCallback(
    (msg: string) => {
      dispatch(AddSuccessAlert(msg))
    },
    [dispatch]
  )

  const showError = React.useCallback(
    (err: any) => {
      dispatch(AddErrorAlert(err))
    },
    [dispatch]
  )

  const wrappedPatch = React.useCallback(
    async ({
      updates,
      message = 'Changes saved'
    }: {
      updates: TaskPatch[]
      message?: string
    }) => {
      const results = await patchTasks(updates)

      // Check if any errors occurred
      const errors: String[] = []
      results.forEach(result => {
        if (result.errors?.length) {
          result.errors.forEach(error => {
            errors.push(error.msg)
          })
        }
      })

      if (errors.length === 0) {
        showMessage(message)
        return
      }

      errors.forEach(msg => showError({ err: { message: msg } }))
    },
    [patchTasks, showMessage, showError]
  )

  return {
    selected,
    numTasks,
    suffix,
    plural,
    patchTasks: wrappedPatch,
    showMessage,
    showError
  }
}

export default useTaskMultiEdit
